/**
 * <product-infinite-scroll>
 *
 * Defilement infini SEO-safe pour la grille de collection.
 *
 * Principe : amelioration progressive posee PAR-DESSUS la pagination serveur
 * de Dawn. La pagination reelle (liens <a href="?page=N">) reste presente dans
 * le DOM ; elle n'est masquee que par JavaScript. Donc :
 *   - sans JS  -> la pagination est visible et fonctionnelle (fallback humain) ;
 *   - crawler  -> les liens ?page=N sont dans le HTML et restent suivables ;
 *   - avec JS  -> on charge la page suivante au scroll et on l'ajoute a la grille.
 *
 * Reutilise la Section Rendering API de Shopify, comme facets.js : on fetch
 * `?page=N&section_id=<id>` puis on extrait le <ul.product-grid> du HTML retourne.
 * Fonctionne pour la collection ET la recherche (meme mecanique facets.js).
 *
 * Quand un filtre/tri est applique, facets.js remplace tout #ProductGridContainer :
 * ce composant est alors detruit puis recree avec une nouvelle data-next-url,
 * donc l'etat (page courante, observer) se reinitialise tout seul.
 *
 * Bouton precedent/suivant : on stocke en sessionStorage UNIQUEMENT le numero
 * de derniere page chargee + la position de scroll (quelques octets). Au retour
 * (navigation back_forward), on re-fetch les pages 2..N et on repositionne le
 * scroll. Stockage minimal, au prix de quelques requetes reseau au retour.
 */
class ProductInfiniteScroll extends HTMLElement {
  connectedCallback() {
    // Cible d'ajout des cartes : le <ul> de la grille. On vise le <ul> plutot
    // que #product-grid car selon le gabarit cet id porte soit sur le <ul>
    // (collection) soit sur le div conteneur (recherche).
    this.list = this.querySelector('ul.product-grid');
    if (!this.list) return;

    // section_id passe explicitement par Liquid (data-section-id) -> aucune
    // dependance a la structure DOM du gabarit.
    this.sectionId = this.dataset.sectionId;
    this.nextUrl = this.dataset.nextUrl || '';
    this.loading = false;
    this.currentPage = 1; // page la plus profonde actuellement chargee
    this.spinner = this.querySelector('.loading__spinner');
    this.pagination = this.querySelector('.pagination-wrapper');

    // Restauration du scroll : on prend le controle ('manual') car la
    // restauration native est INCOHERENTE sur iOS Safari (retour en haut « une
    // fois sur deux » selon que la page vienne ou non du bfcache). On gere donc
    // explicitement les DEUX chemins de retour arriere :
    //  - bfcache (frequent sur iOS) : la page revient intacte SANS re-executer
    //    le JS -> on restaure le scroll dans `pageshow` (event.persisted=true) ;
    //  - vrai rechargement : connectedCallback s'execute -> re-fetch des pages
    //    deja vues (si >= page 2) puis repositionnement du scroll.
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

    // Masquer la pagination serveur visuellement, mais la GARDER dans le DOM
    // (fallback no-JS + crawlers). On utilise un style inline pose par JS :
    // si JS ne tourne pas, ce code ne s'execute pas et la pagination reste visible.
    if (this.pagination) this.pagination.style.display = 'none';

    // Sauvegarder l'etat avant de quitter la page (clic produit, etc.).
    this.boundSaveState = this.saveState.bind(this);
    window.addEventListener('pagehide', this.boundSaveState);

    // Retour depuis le bfcache (DOM deja intact) : restaurer juste le scroll.
    // Le listener survit au gel/restauration de la page et `pageshow` se
    // declenche meme quand connectedCallback ne tourne pas.
    this.boundOnPageShow = this.onPageShow.bind(this);
    window.addEventListener('pageshow', this.boundOnPageShow);

    // Retour via bouton precedent/suivant (hors bfcache) :
    const restoreData = this.getRestoreData();
    if (restoreData && restoreData.page > 1) {
      // Pages profondes : re-fetch des pages 2..N puis repositionnement.
      this.restore(restoreData);
      return;
    }
    if (restoreData) {
      // Page 1 deja rendue par le serveur : on restaure juste le scroll.
      this.scrollToSaved(restoreData.scrollY);
    }

    // Derniere page (ou collection sur une seule page) : rien a observer.
    if (!this.nextUrl) {
      if (this.pagination) this.pagination.remove();
      return;
    }

    this.setupObserver();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.boundSaveState) window.removeEventListener('pagehide', this.boundSaveState);
    if (this.boundOnPageShow) window.removeEventListener('pageshow', this.boundOnPageShow);
  }

  /**
   * Cle de stockage stable pour cette vue de collection : on ignore les
   * parametres `page` et `section_id` pour qu'un retour depuis n'importe quelle
   * page profonde retrouve le meme etat (les filtres/tri restent dans la cle).
   */
  stateKey() {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    return `infinite-scroll:${url.pathname}?${url.searchParams.toString()}`;
  }

  saveState() {
    // Rien a restaurer si on n'a ni charge de pages supplementaires ni scrolle.
    if (this.currentPage <= 1 && window.scrollY <= 0) return;

    // Payload minimal : numero de page + position de scroll (quelques octets).
    try {
      sessionStorage.setItem(
        this.stateKey(),
        JSON.stringify({ page: this.currentPage, scrollY: window.scrollY, t: Date.now() })
      );
    } catch (error) {
      // Quota / navigation privee : on abandonne silencieusement.
    }
  }

  // Lecture de l'etat sauvegarde (valide + recent : 30 min). Renvoie null sinon.
  readState() {
    let raw;
    try {
      raw = sessionStorage.getItem(this.stateKey());
    } catch (error) {
      return null;
    }
    if (!raw) return null;

    let data;
    try {
      data = JSON.parse(raw);
    } catch (error) {
      return null;
    }
    if (!data || Date.now() - data.t > 30 * 60 * 1000) return null;
    return data;
  }

  getRestoreData() {
    // Ne restaurer que lors d'une navigation precedent/suivant.
    const navEntry = performance.getEntriesByType('navigation')[0];
    const isBackForward = navEntry
      ? navEntry.type === 'back_forward'
      : performance.navigation && performance.navigation.type === 2;
    if (!isBackForward) return null;
    return this.readState();
  }

  // Retour depuis le bfcache : la page est intacte, on restaure seulement le
  // scroll (le natif est desactive par scrollRestoration = 'manual').
  onPageShow(event) {
    if (!event.persisted) return;
    const data = this.readState();
    if (data) this.scrollToSaved(data.scrollY);
  }

  scrollToSaved(scrollY) {
    if (!scrollY) return;
    // Double rAF : attendre que la mise en page soit calculee avant de scroller.
    requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo(0, scrollY)));
  }

  /**
   * Re-fetch les pages 2..data.page (page 1 deja rendue par le serveur), les
   * ajoute a la grille, puis repositionne le scroll. Reprend ensuite le
   * defilement infini a partir de la derniere page.
   */
  async restore(data) {
    if (this.spinner) this.spinner.classList.remove('hidden');

    try {
      const pages = [];
      for (let page = 2; page <= data.page; page += 1) pages.push(page);

      // Fetch en parallele (l'append reste ordonne via l'ordre du tableau).
      const responses = await Promise.all(
        pages.map((page) => {
          const url = new URL(window.location.href);
          url.searchParams.set('page', page);
          url.searchParams.set('section_id', this.sectionId);
          return fetch(url.toString()).then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.text();
          });
        })
      );

      responses.forEach((text) => {
        const fetchedDoc = new DOMParser().parseFromString(text, 'text/html');
        const newList = fetchedDoc.querySelector('ul.product-grid');
        if (newList) {
          Array.from(newList.children).forEach((item) => this.list.appendChild(item));
        }
        const nextComponent = fetchedDoc.querySelector('product-infinite-scroll');
        this.nextUrl = nextComponent ? nextComponent.dataset.nextUrl || '' : '';
      });

      this.currentPage = data.page;

      // Cartes restaurees visibles immediatement (pas d'animation a la reprise).
      this.querySelectorAll('.scroll-trigger').forEach((element) => {
        element.classList.remove('scroll-trigger--offscreen');
        element.classList.add('scroll-trigger--cancel');
      });

      // Repositionner le scroll une fois la mise en page calculee.
      this.scrollToSaved(data.scrollY);
    } catch (error) {
      // Echec reseau : on garde la page 1 serveur et on reprend depuis data-next-url.
      this.nextUrl = this.dataset.nextUrl || '';
      this.currentPage = 1;
    } finally {
      if (this.spinner) this.spinner.classList.add('hidden');
    }

    // Reprendre le defilement infini (ou nettoyer si on est en fin de liste).
    if (!this.nextUrl) {
      if (this.pagination) this.pagination.remove();
    } else {
      this.setupObserver();
    }
  }

  setupObserver() {
    // Pas d'IntersectionObserver -> on laisse la pagination visible comme fallback.
    if (!('IntersectionObserver' in window)) {
      if (this.pagination) this.pagination.style.display = '';
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadNext();
      },
      { rootMargin: '600px 0px' }
    );

    // On observe le bloc de statut (toujours present dans la mise en page).
    this.sentinel = this.spinner ? this.spinner.parentElement : this;
    this.observer.observe(this.sentinel);
  }

  async loadNext() {
    if (this.loading || !this.nextUrl) return;
    this.loading = true;
    if (this.spinner) this.spinner.classList.remove('hidden');

    const fetchUrl = new URL(this.nextUrl, window.location.origin);
    fetchUrl.searchParams.set('section_id', this.sectionId);

    try {
      const response = await fetch(fetchUrl.toString());
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

      // Ajouter les nouvelles cartes produit a la grille existante.
      const newList = doc.querySelector('ul.product-grid');
      if (newList) {
        const newItems = Array.from(newList.children);
        newItems.forEach((item) => this.list.appendChild(item));
        this.revealItems(newItems);
      }

      // Memoriser la page chargee (pour la restauration au retour navigateur).
      const loadedPage = parseInt(fetchUrl.searchParams.get('page'), 10);
      if (loadedPage) this.currentPage = loadedPage;

      // Recuperer l'URL de la page suivante depuis le composant fraichement rendu.
      const nextComponent = doc.querySelector('product-infinite-scroll');
      this.nextUrl = nextComponent ? nextComponent.dataset.nextUrl || '' : '';

      // NB : on NE modifie PAS l'URL en `?page=N`. Sur Shopify, charger
      // directement `?page=N` ne rend que cette page (jamais 1->N cumulees) :
      // garder l'URL sur la base de la collection donne un rechargement (F5) et
      // un partage propres. Le bouton precedent/suivant est gere par la
      // restauration sessionStorage (saveState / restore).

      if (!this.nextUrl) {
        // Derniere page atteinte : on arrete d'observer et on retire la pagination
        // devenue inutile (toutes les pages sont desormais dans le DOM).
        if (this.observer) this.observer.disconnect();
        if (this.pagination) this.pagination.remove();
      } else if (this.observer && this.sentinel) {
        // Si la sentinelle est toujours visible (page courte, append rapide),
        // l'observer ne se re-declenche pas tout seul : on la re-observe pour
        // forcer un nouveau test d'intersection et enchainer les pages.
        this.observer.unobserve(this.sentinel);
        this.observer.observe(this.sentinel);
      }
    } catch (error) {
      // Erreur reseau/serveur : on restaure la pagination crawlable pour que
      // l'utilisateur puisse continuer manuellement.
      if (this.observer) this.observer.disconnect();
      if (this.pagination) this.pagination.style.display = '';
    } finally {
      this.loading = false;
      if (this.spinner) this.spinner.classList.add('hidden');
    }
  }

  /**
   * Reveler les items ajoutes via l'animation scroll-trigger de Dawn,
   * en n'observant QUE les nouveaux elements (pas de re-declenchement des anciens).
   */
  revealItems(items) {
    if (!('IntersectionObserver' in window) || typeof onIntersection !== 'function') return;

    const observer = new IntersectionObserver(onIntersection, { rootMargin: '0px 0px -50px 0px' });
    items.forEach((item) => {
      if (item.classList && item.classList.contains('scroll-trigger')) observer.observe(item);
      if (item.querySelectorAll) {
        item.querySelectorAll('.scroll-trigger').forEach((child) => observer.observe(child));
      }
    });
  }
}

customElements.define('product-infinite-scroll', ProductInfiniteScroll);
